<template>
  <div class="fixed-income-products" data-testid="fixed-income-products">
    <div class="fixed-income-products__header-background">
      <section
        :class="{
          'fixed-income-products__header': true,
          'fixed-income-products__header--hover':
            isSearchBarHover && !isSearchBarFocus
        }"
      >
        <div class="fixed-income-products__header-container">
          <SearchBar
            v-model="searchValue"
            class="fixed-income-products__search-bar"
            :dropdown-items="[]"
            placeholder="Buscar produto"
            @mouseenter="isSearchBarHover = true"
            @mouseleave="isSearchBarHover = false"
            @focus="isSearchBarFocus = true"
            @blur="isSearchBarFocus = false"
            @input="onInputChange"
          />
          <FilterSelectButton
            v-if="filters.length > 0"
            label="Filtros"
            :show-chevron-icon="false"
            :selected-counter="filtersCount"
            @click="openFilters"
          />
        </div>
      </section>
    </div>
    <LoaderIndicator
      v-if="isLoading"
      class="fixed-income-products__loader"
      size="32"
      with-container
    />
    <HelperViewStatus
      v-else-if="hasError"
      class="fixed-income-products__helper"
      title="Não foi possível carregar as informações"
      description="Aguarde alguns minutos e tente novamente."
      button-label="Tentar novamente"
      status="alert"
      @click="retry"
    />
    <section v-else class="fixed-income-products__table">
      <MarketStatus
        v-if="marketStatus && !isEnabledSecondaryMarket"
        :status="marketStatus"
      />
      <EmptyHelper
        v-if="isEmpty"
        class="fixed-income-products__helper"
        :type="emptyDataType"
        :search-value="searchValue"
        :filters-count="filtersCount"
        @click="handleClear"
      />
      <DataTable
        v-else
        :columns="columnsTable"
        :data="products"
        show-divider
        :status="isSorting ? 'loading' : 'default'"
        has-interaction
        @on-sort="onSort"
        @on-row-click="handleProductClick"
      />
      <LoaderIndicator
        v-if="isScrolling"
        class="fixed-income-products__table-loader"
        size="32"
        with-container
      />
    </section>
    <ButtonIconPrimary
      v-if="hasScrolled"
      :class="scrolledButtonClass"
      label="Ir para o inicio"
      icon="EA0010"
      size="large"
      @click="goToTop"
    />
    <SidebarFilters
      v-if="filters.length > 0"
      :is-open="isFiltersOpen"
      :filters="filters"
      :clear="clearFilters"
      @apply-filters="applyFilters"
      @close="isFiltersOpen = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';
import { debounce } from 'lodash-es';
import { useRoute } from 'vue-router/composables';

import {
  SearchBar,
  LoaderIndicator,
  HelperViewStatus,
  DataTable,
  ButtonIconPrimary
} from '@warrenbrasil/nebraska-web';
import { FilterSelectButton } from '@/modules/common/components/FilterSelectButton';
import {
  EmptyHelper,
  MarketStatus,
  SidebarFilters
} from '@/modules/catalog/components';

import { registerObserver } from '@/modules/catalog/views/Products/helpers/registerObserver';
import { columns } from './columns';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import {
  IFixedIncomeProduct,
  IFixedIncomeFilter,
  IPagination,
  IProductsParams,
  IMarketStatus,
  getFixedIncomeInstruments,
  getFixedIncomeProductsV2
} from '@/modules/catalog/services/products';

interface Props {
  isBuilder: boolean;
}
const props = defineProps<Props>();

interface EmitEvents {
  (event: 'select-product', id: string): string;
}
const emit = defineEmits<EmitEvents>();

const route = useRoute();
const products = ref<IFixedIncomeProduct[]>([]);
const filters = ref<IFixedIncomeFilter[]>([]);
const pagination = ref<IPagination>();
const marketStatus = ref<IMarketStatus>();

const params = ref<IProductsParams>({
  pageIndex: 1,
  pageSize: 10
});

const isLoading = ref(true);
const isScrolling = ref(false);
const isSorting = ref(false);
const hasError = ref(false);
const isSearchBarHover = ref(false);
const isSearchBarFocus = ref(false);

const searchValue = ref('');
const isFiltersOpen = ref(false);
const clearFilters = ref(false);
const filtersCount = ref(0);
const selectedFilters = ref<Record<string, string | string[]>>({});
const scrollTop = ref(document.documentElement.scrollTop);

const columnsTable = computed(() => {
  return columns(
    pagination.value!.totalRecords,
    marketStatus.value?.isMarketOpen || false,
    isEnabledSecondaryMarket.value
  );
});

const hasScrolled = computed(() => {
  return scrollTop.value >= 100;
});

const isEmpty = computed(() => {
  return products.value.length === 0;
});

const emptyDataType = computed(() => {
  if (searchValue.value) return 'search';

  if (filtersCount.value > 0) return 'filter';

  return 'empty';
});

const scrolledButtonClass = computed(() => {
  return props.isBuilder
    ? 'fixed-income-products__button builder'
    : 'fixed-income-products__button';
});

const isEnabledSecondaryMarket = computed(() => {
  return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_SECONDARY_MARKET);
});

const onInputChange = debounce(handleSearch, 500);

function openFilters() {
  isFiltersOpen.value = true;
}

function closeFilters() {
  isFiltersOpen.value = false;
}

async function handleClear() {
  if (searchValue.value) {
    searchValue.value = '';

    handleSearch();
  } else clearFilters.value = true;
}

async function applyFilters(
  applyedFilters: IFixedIncomeFilter[],
  count: number
) {
  clearFilters.value = false;
  filtersCount.value = count;

  if (applyedFilters.length === 0) {
    selectedFilters.value = {};
  } else {
    selectedFilters.value = {};

    applyedFilters.forEach(filter => {
      // SidebarFilters now transforms maturityDate into minimumMaturity and maximumMaturity
      // So we just assign them directly
      selectedFilters.value[filter.value] = filter.selectedFilters;
    });
  }

  params.value = {
    ...params.value,
    pageIndex: 1,
    sortField: undefined,
    sortDirection: undefined
  };

  closeFilters();

  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

async function onSort(index: number, isSort: boolean, value: string) {
  const sortDirection =
    index === -1 ? undefined : isSort ? 'Ascending' : 'Descending';
  const sortField = index === -1 ? undefined : value;

  params.value = {
    ...params.value,
    pageIndex: 1,
    sortField,
    sortDirection
  };

  await fetchFixedIncomeProducts(false, false, true);
  registerObserver(fetchFixedIncomeProducts);
}

async function handleSearch() {
  params.value = {
    pageSize: 10,
    pageIndex: 1,
    searchTerm: searchValue.value
  };

  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

async function retry() {
  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

function goToTop() {
  document.documentElement.scrollTo(0, 0);
}

async function fetchFixedIncomeProducts(
  loading = true,
  scrolling = false,
  sorting = false
) {
  try {
    isLoading.value = loading;
    isScrolling.value = scrolling;
    isSorting.value = sorting;
    hasError.value = false;

    if (isScrolling.value) {
      if (!pagination.value?.nextPage) return;

      params.value = {
        ...params.value,
        pageIndex: pagination.value.nextPage
      };
    }

    const data = isEnabledSecondaryMarket.value
      ? await getFixedIncomeProductsV2(selectedFilters.value, params.value)
      : await getFixedIncomeInstruments(selectedFilters.value, params.value);

    filters.value = data.filters;
    pagination.value = data.pagination;
    if (!isEnabledSecondaryMarket.value)
      marketStatus.value = data?.marketStatus;

    const items = data.items.map(item => {
      return {
        ...item
      };
    });

    if (isScrolling.value) {
      products.value.push(...items);
      return;
    }

    products.value = items;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
    isScrolling.value = false;
    isSorting.value = false;
  }
}

function handleProductClick(product: IFixedIncomeProduct) {
  emit('select-product', product.productId);

  const routerName = props.isBuilder
    ? `catalog/builder/${
        route.params.portfolioId ? route.params.portfolioId + '/' : ''
      }fixed-income/${product.productId}`
    : `products-view/catalog/fixed-income/${product.productId}`;

  window.history.pushState({}, '', `/app/#/${routerName}`);
}

function handleScrollTop() {
  scrollTop.value = document.documentElement.scrollTop;
}

onMounted(async () => {
  await fetchFixedIncomeProducts();

  registerObserver(fetchFixedIncomeProducts);
});

onBeforeMount(() => {
  window.addEventListener('scroll', handleScrollTop);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScrollTop);
});
</script>

<style lang="less" scoped>
.fixed-income-products {
  :deep(.input-wrapper) {
    border: none;
    padding: 0;
  }

  :deep(.bottom-helper) {
    display: none;
  }

  :deep(.table__header .table-row) {
    height: 72px;
  }

  :deep(.table__header .table-row .header-cell) {
    .text-p-5();
    font-weight: 600;
    text-align: right;
    padding: @size-spacing-x350 @size-spacing-x400;
  }

  :deep(.header-cell__icon) {
    margin-left: @size-spacing-x300;
  }

  :deep(.table-row .cell p) {
    &:first-of-type {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__header {
    background-color: @background-tertiary;
    width: 100%;

    &--hover {
      background-color: @background-hover;
    }
  }

  &__header-background {
    position: sticky;
    top: 64px;
    left: 0;
    z-index: 20;
    background-color: @background-secondary;

    @media only screen and (max-width: 640px) {
      top: 60px;
    }
  }

  &__header-container {
    padding: 0 @size-spacing-x1100;
    max-width: 1152px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__search-bar {
    width: 890px;
    max-width: 890px;

    &:hover {
      cursor: pointer;
    }

    :deep(.trailing-icon) {
      display: none;
    }
  }

  &__helper {
    margin: @size-spacing-x800 auto 0;
    justify-content: center;
  }

  &__table {
    padding: 0 @size-spacing-x1100;
    max-width: 1152px;
    margin: @size-spacing-x800 auto;

    :deep(.table__body .table-row) {
      height: 90px;
    }

    &-loader {
      margin: @size-spacing-x800 auto @size-spacing-x800;
      min-height: 0 !important;
    }
  }

  &__button {
    position: fixed;
    bottom: 32px;
    right: 72px;

    &.builder {
      bottom: 112px;
    }

    @media only screen and (min-width: 1150px) {
      right: calc((100vw - 1008px) / 2);
    }
  }
}
</style>
